var _a;
import { EIntegratorWebhookLogOutcome, EIntegratorWebhookLogResendReason, EIntegratorWebhookLogResendStatus, EIntegratorWebhookLogSortBy, EIntegratorWebhookLogSortOrder, EIntegratorWebhookType, } from "@rentiohq/shared-frontend/dist/types/integratorWebhookLog.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
// What the API accepts in one resend; the selection is capped at the same number.
export var MAX_RESEND_COUNT = 100;
export var PAGE_SIZE = 20;
var KNOWN_REASONS = Object.values(EIntegratorWebhookLogResendReason);
/**
 * The i18n key for a resend refusal, or undefined for a code this build does not know yet - the
 * caller then shows the raw code rather than a key that renders as a dotted string.
 */
export var getResendReasonLabelKey = function (reason) {
    if (!reason || !KNOWN_REASONS.includes(reason)) {
        return undefined;
    }
    return "integrator_webhook_log.resend.reason.".concat(reason.toLowerCase());
};
export var getOutcomeAppearance = function (outcome) {
    return outcome === EIntegratorWebhookLogOutcome.Succeeded ? "success" : "error";
};
export var getResendStatusAppearance = function (status) {
    switch (status) {
        case EIntegratorWebhookLogResendStatus.Ok:
            return "success";
        case EIntegratorWebhookLogResendStatus.Failed:
            return "error";
        case EIntegratorWebhookLogResendStatus.Skipped:
            return "warning";
    }
};
/**
 * A stored body for reading: indented when it is JSON, as stored otherwise. Integrators answer
 * with HTML error pages as often as with JSON, and a body cut off at the storage cap is never
 * valid JSON, so a parse failure is the normal case rather than an error.
 */
export var formatBodyForDisplay = function (body) {
    if (body === null || body === "") {
        return undefined;
    }
    try {
        return JSON.stringify(JSON.parse(body), null, 2);
    }
    catch (_a) {
        return body;
    }
};
export var countResultsByStatus = function (results) {
    var _a;
    var counts = (_a = {},
        _a[EIntegratorWebhookLogResendStatus.Ok] = 0,
        _a[EIntegratorWebhookLogResendStatus.Failed] = 0,
        _a[EIntegratorWebhookLogResendStatus.Skipped] = 0,
        _a);
    results.forEach(function (result) {
        counts[result.status] += 1;
    });
    return counts;
};
// A date input yields "yyyy-MM-dd" in the viewer's own day; the API filters on instants, so
// the upper bound has to include the whole of that day.
export var getDayStartIso = function (day) {
    return day ? new Date("".concat(day, "T00:00:00")).toISOString() : undefined;
};
export var getDayEndIso = function (day) {
    return day ? new Date("".concat(day, "T23:59:59.999")).toISOString() : undefined;
};
// The API's own default, stated so the table can mark the date column as sorted.
export var DEFAULT_SORT = {
    sortBy: EIntegratorWebhookLogSortBy.CreatedAt,
    sortOrder: EIntegratorWebhookLogSortOrder.Desc,
};
/**
 * The sort a header click asks for. The table already toggles the direction when the sorted
 * column is clicked again; a column the API cannot order by, or no direction, keeps the sort.
 */
export var getSortAfterHeaderClick = function (sortBy, direction) {
    if (!sortBy || direction === "none") {
        return undefined;
    }
    return {
        sortBy: sortBy,
        sortOrder: direction === "ASC"
            ? EIntegratorWebhookLogSortOrder.Asc
            : EIntegratorWebhookLogSortOrder.Desc,
    };
};
// Contract and contact reuse the generic words; "property" needs its own key, because the French
// of the generic one means ownership rather than the house a webhook is about.
var WEBHOOK_TYPE_LABEL_KEYS = (_a = {},
    _a[EIntegratorWebhookType.Application] = "integrator_webhook_log.webhook_type.application",
    _a[EIntegratorWebhookType.Document] = "integrator_webhook_log.webhook_type.document",
    _a[EIntegratorWebhookType.Property] = "integrator_webhook_log.webhook_type.property",
    _a[EIntegratorWebhookType.Contract] = "system.contract",
    _a[EIntegratorWebhookType.Contact] = "system.contact",
    _a[EIntegratorWebhookType.Payment] = "integrator_webhook_log.webhook_type.payment",
    _a);
/**
 * The i18n key for a webhook type, or undefined for a type this build does not know - including
 * the stored `entity` of a row whose body names no entity, which has no label of its own.
 */
export var getWebhookTypeLabelKey = function (webhookType) {
    return WEBHOOK_TYPE_LABEL_KEYS[webhookType];
};
var EMPTY_VALUE = "—";
var ENTITY_WEBHOOK_TYPE = "entity";
// A type this build does not know yet shows as stored rather than as a raw key; the bare `entity`
// of a row whose body names no entity shows as empty rather than as a word the screen never uses.
export var getWebhookTypeLabel = function (webhookType) {
    var key = getWebhookTypeLabelKey(webhookType);
    if (key) {
        return getLocalizedText(key);
    }
    return webhookType === ENTITY_WEBHOOK_TYPE ? EMPTY_VALUE : webhookType;
};
